'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchRecentOffers } from '@/utils/offers'
import { collectionLookup, groupInscriptionBids, toRealBook, type RealBidBook } from '@/utils/bid-book-real'
import type { BidActivity, BidCollection } from '@/utils/bids'

const POLL_MS = 20_000

export type BidBookStatus = 'loading' | 'ready' | 'error'

/**
 * The live bid book shared by /bids and the home activity rail: the funded
 * offers the page arrived with, refreshed from the offers API while bidding
 * is open, plus whatever the viewer just did in this tab.
 */
export default function useBidBook({
	collections,
	book,
	realOffers
}: {
	collections: BidCollection[]
	/** The book as the server read it; null when the offers API could not be reached. */
	book: RealBidBook | null
	/** Poll the offers API for funded bids (only where offers are enabled). */
	realOffers: boolean
}) {
	const [real, setReal] = useState<RealBidBook | null>(book)
	const [status, setStatus] = useState<BidBookStatus>(book ? 'ready' : 'loading')
	const [pulses, setPulses] = useState<Record<string, number>>({})
	// What the viewer placed in this tab, shown until a poll carries it.
	const [local, setLocal] = useState<BidActivity[]>([])
	const [attempt, setAttempt] = useState(0)
	const seen = useRef<Set<string> | null>(
		book ? new Set([...book.collectionBids, ...book.inscriptionBids].map((b) => b.id)) : null
	)

	useEffect(() => {
		if (!realOffers) return
		let cancelled = false
		const lookup = collectionLookup(new Map(collections.map((c) => [c.slug, c])), { cache: 'no-store' })
		const load = async () => {
			try {
				const offers = await fetchRecentOffers(300)
				const next = await toRealBook(offers, lookup)
				if (cancelled) return
				// A bid that arrived since the last read flashes its tile.
				const ids = [...next.collectionBids, ...next.inscriptionBids].map((b) => b.id)
				const known = seen.current
				if (known) {
					const fresh = ids.filter((id) => !known.has(id))
					if (fresh.length) {
						const now = Date.now()
						setPulses((p) => ({ ...p, ...Object.fromEntries(fresh.map((id) => [id, now])) }))
					}
				}
				seen.current = new Set(ids)
				setReal(next)
				setStatus('ready')
			} catch {
				// Keep the last book on a failed refresh; only a first read can fail visibly.
				if (!cancelled) setStatus((s) => (s === 'ready' ? s : 'error'))
			}
		}
		void load()
		const t = setInterval(load, POLL_MS)
		return () => {
			cancelled = true
			clearInterval(t)
		}
	}, [realOffers, collections, attempt])

	const collectionBids = useMemo(
		() => [...(real?.collectionBids ?? [])].sort((a, b) => b.priceSats - a.priceSats),
		[real]
	)
	const groups = useMemo(() => groupInscriptionBids(real?.inscriptionBids ?? []), [real])
	const activity = useMemo(() => {
		const base = real?.activity ?? []
		const carried = new Set(base.map((a) => a.id))
		return [...base, ...local.filter((a) => !carried.has(a.id))].sort((a, b) => b.at - a.at).slice(0, 80)
	}, [real, local])
	const addActivity = useCallback(
		(events: BidActivity[]) => setLocal((prev) => [...events, ...prev].slice(0, 60)),
		[]
	)
	const retry = useCallback(() => {
		setStatus('loading')
		setAttempt((n) => n + 1)
	}, [])

	return {
		collectionBids,
		groups,
		activity,
		pulses,
		/** Where bidding is closed there is nothing to wait for: show what the server read. */
		status: realOffers || real ? status : ('ready' as BidBookStatus),
		addActivity,
		retry
	}
}
